import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import useTourV2SnapshotDetails from 'hooks/TourV2/useTourV2SnapshotDetails'
import { useAppDispatch } from 'hooks/reduxHooks'
import { formatTourV2SnapshotRoom } from 'lib/format/formatTourV2SnapshotRoom'
import TextButton from 'components/Luxkit/Button/TextButton'
import Group from 'components/utils/Group'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import StatusLabel from 'components/Luxkit/Label/StatusLabel'
import LineCompassIcon from 'components/Luxkit/Icons/line/LineCompassIcon'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import LesaPurchaseSummaryContainer from './Common/LesaPurchaseSummaryContainer'
import LesaPurchaseSummaryHeader from './Common/LesaPurchaseSummaryHeader'
import LesaPurchaseSummarySeparator from './Common/LesaPurchaseSummarySeparator'
import LesaPurchaseSummarySkeleton from './Common/LesaPurchaseSummarySkeleton'
import LesaPurchaseSummaryItineraryDates from './Common/LesaPurchaseSummaryItineraryDates'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { ITEM_STATUS_CANCELLED, ITEM_STATUS_COMPLETED, ITEM_STATUS_PENDING } from 'constants/cart'
import Action from '../../Chat/MessageMeta/Action'
import { filterActions } from '../../../utils/actionsUtils'
import TourV2FinePrintModal from 'components/Common/OrderView/OrderItemSummaries/TourV2ItemSummary/TourV2FinePrintModal'
import ModalContext from 'contexts/ModalContext'
import LesaPurchaseSummaryTitle from './Common/LesaPurchaseSummaryTitle'
import { addChatMessage } from 'actions/supportAssistant/supportAssistantActions'
import useTourV2Snapshot from 'hooks/TourV2/useTourV2Snapshot'

interface Props {
  order: App.Order;
  isPastOrder: boolean;
  hideSelect?: boolean;
  actions?: Array<App.ChatMessageMetaContent>;
  payload?: App.ChatMessageMetaPayload;
  componentsMap: Map<string | undefined, App.ChatMessageMetaContent>;
}

function LesaTourV2PurchaseSummary(props: Props) {
  const { order, isPastOrder, hideSelect, actions, payload, componentsMap } = props
  const showModal = useContext(ModalContext)
  const orderItem = order.tourItems[0]
  const dispatch = useAppDispatch()
  const [snapshot] = useTourV2Snapshot(orderItem?.reservationId)

  const {
    formattedStartDate,
    formattedEndDate,
    daysCount,
    countriesCount,
    placesCount,
  } = useTourV2SnapshotDetails(snapshot)

  const mergedStatus = useMemo((): { status: App.OrderItemStatus | undefined, description: string | undefined} => {
    if (!snapshot) return { status: undefined, description: undefined }

    const isCompleted = orderItem.status === ITEM_STATUS_COMPLETED && snapshot?.status === 'confirmed'

    if (isCompleted) {
      return { status: ITEM_STATUS_COMPLETED, description: 'Booking confirmed' }
    }

    const isCancelled = orderItem.status === ITEM_STATUS_CANCELLED || snapshot?.status === 'cancelled'

    if (isCancelled) {
      return { status: ITEM_STATUS_CANCELLED, description: 'Booking cancelled' }
    }

    return { status: ITEM_STATUS_PENDING, description: 'Awaiting confirmation' }
  }, [orderItem, snapshot])

  const openFinePrintModal = useCallback(() => {
    showModal(<TourV2FinePrintModal
      finePrint={snapshot?.finePrint}
      subtitle={snapshot?.seasonName}
    />)
  }, [showModal, snapshot])

  useEffect(() => {
    if (componentsMap?.get('fine-print-modal')) {
      openFinePrintModal()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentsMap])

  const handleSelectClick = useCallback(() => {
    dispatch(addChatMessage('', { action: 'selectOrder', payload: { id_orders: order.id } }))
  }, [dispatch, order.id])

  if (!snapshot) {
    return <LesaPurchaseSummarySkeleton />
  }

  const orderActions = actions?.filter(action => filterActions(action, isPastOrder, ['listInclusions', 'changeRoom']))

  return (
    <>
      {(!!componentsMap?.get('list-orders') || !!componentsMap?.get('selectOrder')) && (
        <LesaPurchaseSummaryContainer>
          <LesaPurchaseSummaryHeader>
            <BodyTextBlock lineClamp={2} variant="small" startIcon={<LineCompassIcon size="XS" />}>Tours</BodyTextBlock>
            {!isPastOrder && <StatusLabel variant="confirmation">Upcoming</StatusLabel>}
            {isPastOrder && <StatusLabel variant="pending">Past</StatusLabel>}
          </LesaPurchaseSummaryHeader>
          <Group direction="vertical" tabletDirection="horizontal" horizontalAlign="space-between" verticalAlign="end" gap={16}>
            <Group direction="vertical" gap={4}>
              <CaptionTextBlock
                variant="medium"
                colour="neutral-three"
              >
                {snapshot.startLocation === snapshot.endLocation ? <>Starts and ends in <strong>{snapshot.startLocation}</strong></> : <>Starts in <strong>{snapshot.startLocation}</strong>. Ends in <strong>{snapshot.endLocation}</strong></>}
              </CaptionTextBlock>
              <LesaPurchaseSummaryTitle orderId={order.id}>
                { snapshot.seasonName }
              </LesaPurchaseSummaryTitle>{' '}
              <LesaPurchaseSummarySeparator>
                {!!daysCount && <BodyText as="span" variant="small" colour="neutral-two">{daysCount}</BodyText>}
                {!!countriesCount && <BodyText as="span" variant="small" colour="neutral-two">{countriesCount}</BodyText>}
                {!!placesCount && <BodyText as="span" variant="small" colour="neutral-two">{placesCount}</BodyText>}
              </LesaPurchaseSummarySeparator>
              <LesaPurchaseSummaryItineraryDates
                status={mergedStatus.status}
                showReservationDates={!!formattedStartDate}
                startDate={formattedStartDate}
                endDate={formattedEndDate}
                bookingNumber={orderItem?.bookingId}
              />
              {snapshot?.rooms.map((room, index) => (
                <LesaPurchaseSummarySeparator key={index}>
                  <BodyText as="span" variant="small" colour="neutral-three">{formatTourV2SnapshotRoom(room)}</BodyText>
                  <BodyText as="span" variant="small" colour="neutral-three">{room.roomType}</BodyText>
                </LesaPurchaseSummarySeparator>
              ))}
            </Group>
            {!hideSelect &&
              <TextButton onClick={handleSelectClick} fit="mobile-full-width" size="small" kind="secondary">
                Select
              </TextButton>
            }
          </Group>
        </LesaPurchaseSummaryContainer>
      )}
      {!!orderActions?.length &&
        <Group fullWidth wrap="wrap" horizontalAlign="start" direction="vertical" tabletDirection="horizontal" gap={8}>
          {orderActions.map((action) => (
            <Action key={action.action?.key} action={action} payload={payload} />
          ))}
        </Group>}
    </>
  )
}

export default LesaTourV2PurchaseSummary
